import { hasValue } from './general.helper';
import {
  numberAsCompact,
  numberToBatches,
  numberWithOrdinal,
} from './number.helper';

/**
 * Converts a string to PascalCase.
 * @function toPascalCase
 * @param {string} str - The string to convert.
 * @returns {string} The PascalCase string.
 */
const toPascalCase = (str: string) => {
  return str.replace(/([A-Z])/g, ' $1').trim();
};

/**
 * Capitalizes the first letter of a string.
 * @function capitalize
 * @param {string} str - The string to capitalize.
 * @returns {string} The capitalized string.
 */
const capitalize = (str: string) => {
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
};

/**
 * Converts the first letter of a string to lowercase.
 * @function lowercase
 * @param {string} str - The string to convert.
 * @returns {string} The string with the first letter in lowercase.
 */
const lowercase = (str: string) => {
  return `${str.charAt(0).toLowerCase()}${str.slice(1)}`;
};

/**
 * Checks if a string contains whitespace.
 * @function hasWhitespace
 * @param {string} str - The string to check.
 * @returns {boolean} True if the string contains whitespace, false otherwise.
 */
const hasWhitespace = (str: string) => {
  return str.indexOf(' ') >= 0;
};

/**
 * Normalizes accents in a string.
 * @function normalizeAccents
 * @param {string} str - The string to normalize.
 * @returns {string} The normalized string.
 */
const normalizeAccents = (str: string) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

/**
 * Compares a string to a value for equality.
 * @function equals
 * @param {string} str - The string to compare.
 * @param {string | number | null} value - The value to compare against.
 * @param {boolean} [insensitive=true] - Whether the comparison should be case-insensitive.
 * @returns {boolean} True if the values are equal, false otherwise.
 */
const stringEquals = (
  str: string,
  value: string | number | null,
  insensitive = true,
) => {
  if (!hasValue(value)) return false;

  if (insensitive)
    return str.toString().toLowerCase() == value!.toString().toLowerCase();

  return str.toString() == value!.toString();
};

/**
 * Generates a DJB hash for a string.
 * @function hashDJB
 * @param {string} str - The string to hash.
 * @param {boolean} [normalize=true] - Whether to normalize the string before hashing.
 * @returns {number} The DJB hash of the string.
 */
const hashDJB = (str: string, normalize = true) => {
  let value = str;

  if (normalize) {
    value = value.trim().toLowerCase();
  }

  let hash = 5381;
  let i = value.length;

  while (i) {
    hash = (hash * 33) ^ value.charCodeAt(--i);
  }

  return hash >>> 0;
};

/**
 * Adds an ordinal suffix to a string representing a number.
 * @function withOrdinal
 * @param {string} str - The string to add the ordinal suffix to.
 * @returns {string} The string with the ordinal suffix.
 */
const withOrdinal = (str: string) => {
  return numberWithOrdinal(Number(str));
};

/**
 * Formats a string representing a number in compact notation.
 * @function asCompact
 * @param {string} str - The string to format.
 * @returns {string} The formatted string in compact notation.
 */
const asCompact = (str: string) => {
  return numberAsCompact(Number(str || 0));
};

/**
 * Divides a string representing a number into batches of a specified step size.
 * @function toBatches
 * @param {string} str - The string to divide into batches.
 * @param {number} step - The step size for each batch.
 * @returns {Array<{ start: number; end: number }>} An array of batches with start and end values.
 */
const toBatches = (str: string, step: number) => {
  return numberToBatches(Number(str || 0), step);
};

/**
 * Strips URLs from a string.
 * @function stripUrls
 * @param {string} str - The string to strip URLs from.
 * @param {string} [replacement=''] - The replacement string for the URLs.
 * @returns {string} The string with URLs stripped.
 */
const stripUrls = (str: string, replacement = '') => {
  if (!str) return str;
  const regex =
    /(([\w+]+\:\/\/)?([\w\d-]+\.)*[\w-]+[\.\:]\w+([\/\?\=\&\#\.]?[\w-]+)*\/?)/gm;
  return str.replace(regex, replacement);
};

export {
  stripUrls,
  toPascalCase,
  capitalize,
  lowercase,
  hasWhitespace,
  normalizeAccents,
  stringEquals,
  hashDJB,
  withOrdinal,
  asCompact,
  toBatches,
};
